/* eslint-disable complexity */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import {
    Text, Link, Divider, Box, Flex
} from 'components/ui';
import FormValidator from 'utils/FormValidator';
import localeUtils from 'utils/LanguageLocale';
import {
    mediaQueries, colors, radii, space
} from 'style/config';
import Markdown from 'components/Markdown/Markdown';

const { getCurrentCountry } = localeUtils;
const country = getCurrentCountry();

const PickupPersonSummary = ({ pickupPerson, isGuestCheckout, isCanada }) => {
    return (
        <>
            <Text
                is='p'
                fontSize={'base'}
                lineHeight={1.3}
                marginTop={1}
            >
                {pickupPerson?.firstName} {pickupPerson?.lastName}
            </Text>
            {!isCanada && (
                <Text
                    is='p'
                    lineHeight={1.3}
                    fontSize={'base'}
                >
                    {FormValidator.getFormattedPhoneNumber(pickupPerson?.phoneNumber)}
                </Text>
            )}
            {isCanada && (
                <>
                    <Text
                        is='p'
                        fontSize={'base'}
                        lineHeight={1.3}
                    >
                        {pickupPerson?.addressLine1}
                    </Text>
                    <Text
                        is='p'
                        lineHeight={1.3}
                        fontSize={'base'}
                    >
                        {pickupPerson?.city}, {pickupPerson?.state} {pickupPerson.postalCode}{' '}
                    </Text>
                </>
            )}
            {isGuestCheckout && pickupPerson?.email && (
                <Text
                    is='p'
                    fontSize={'base'}
                    lineHeight={1.3}
                >
                    {pickupPerson?.email}
                </Text>
            )}
        </>
    );
};

class ShipToAccessPoint extends BaseClass {
    state = {
        pickupPerson: {
            firstName: null,
            lastName: null,
            phoneNumber: null,
            email: null,
            addressLine1: null,
            addressLine2: null,
            city: null,
            state: null,
            postalCode: null,
            country: null
        },
        statesList: [],
        showCityAndState: false,
        invalidPostalCode: false
    };

    createShippingAddress = (halAddressPayload, currentHalAddress) => {
        this.props.createShippingAddress(halAddressPayload, currentHalAddress, this);
    };

    initPickupPersonData = () => {
        const { shippingAddress, user } = this.props;

        this.setState({
            pickupPerson: {
                firstName: shippingAddress?.firstName || user?.firstName,
                lastName: shippingAddress?.lastName || user?.lastName,
                phoneNumber: shippingAddress?.phone || user?.phoneNumber,
                email: shippingAddress?.email,
                addressLine1: shippingAddress?.pickUpPersonAddress1,
                addressLine2: shippingAddress?.pickUpPersonAddress2,
                city: shippingAddress?.pickUpPersonCity,
                state: shippingAddress?.pickUpPersonState,
                postalCode: shippingAddress?.pickUpPersonPostalCode || shippingAddress?.postalCode
            }
        });
    };

    getStatesList = (showCityAndState = false) => {
        const { getStateList } = this.props;

        getStateList(country, statesList => {
            // remove option 0 - 'select a province' from API response
            this.setState({ statesList: statesList.slice(1), showCityAndState });
        });
    };

    componentDidMount() {
        this.initPickupPersonData();
        this.getStatesList();
    }

    componentDidUpdate(prev) {
        const { isCanada, shippingAddress } = this.props;

        // Only enter this flow when we change from
        // editMode = false to editMode = true
        if (prev.shippingAddress !== shippingAddress) {
            this.initPickupPersonData();

            if (isCanada) {
                // Show the City and Province inputs when
                // entering edit mode. Postal Code must be
                // part of the shippingAddress at this point.
                const showCityAndState = Boolean(shippingAddress?.pickUpPersonPostalCode);
                this.getStatesList(showCityAndState);
            }
        }
    }

    handleResetAccessPoint = () => {
        const { resetAccessPoint } = this.props;

        resetAccessPoint(true);
    };

    editPickuPerson = () => {
        this.props.callback(true);
    };

    render() {
        const { editMode, isGuestCheckout, localization, isCanada } = this.props;

        const { pickupPerson: pickupPersonLabel, idRequired, idRequiredEditMode } = localization;

        const { pickupPerson } = this.state;
        const fullNameCaptured = Boolean(pickupPerson?.firstName && pickupPerson.lastName);
        const idRequiredText = editMode ? idRequiredEditMode : idRequired;

        return (
            <Box marginTop={4}>
                {!editMode && fullNameCaptured && <Divider marginBottom={4} />}
                <Flex justifyContent='space-between'>
                    <Text
                        display='block'
                        fontWeight='bold'
                        fontSize={['base', 'md']}
                    >
                        {pickupPersonLabel}
                    </Text>
                    {!editMode && fullNameCaptured && (
                        <Link
                            color='blue'
                            lineHeight='tight'
                            children={'Edit'}
                            onClick={this.editPickuPerson}
                        />
                    )}
                </Flex>
                {fullNameCaptured && (
                    <PickupPersonSummary
                        pickupPerson={pickupPerson}
                        isGuestCheckout={isGuestCheckout}
                        isCanada={isCanada}
                    />
                )}
                <Box css={[!editMode && fullNameCaptured && styles.requiredTextContainer]}>
                    <Markdown
                        fontSize={['sm', 'base']}
                        lineHeight={['1.2', '1.5']}
                        content={editMode ? idRequiredText.replace(/\*/g, '') : idRequiredText}
                    />
                </Box>
            </Box>
        );
    }
}

const styles = {
    addressInput: {
        root: {
            [mediaQueries.md]: {
                display: 'grid',
                gridColumnStart: 1,
                gridColumnEnd: 3
            }
        }
    },
    requiredTextContainer: {
        backgroundColor: colors.nearWhite,
        borderRadius: radii[2] + 'px',
        padding: `${space[2]}px ${space[3]}px`,
        marginTop: space[4] + 'px'
    },

    button: {
        width: '48%',
        maxWidth: 224
    },

    postalCode: {
        [mediaQueries.smMax]: {
            marginBottom: -space[4]
        }
    }
};

ShipToAccessPoint.defaultProps = {
    shippingAddress: {},
    isCanada: false
};

export default wrapComponent(ShipToAccessPoint, 'ShipToAccessPoint', true);
